import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

// Default to AC-16051 (7stars Partners - Raketech34)
const DEFAULT_NETWORK_ID = '4cc4ee4c-6176-42cc-aea0-b9942164734e';
const DEFAULT_ACCOUNT = 'AC-16051';

interface MonthlyData {
  stat_month?: string;
  month?: string;
  clicks: number;
  signups?: number;
  registrations?: number;
  ftds?: number;
  first_time_depositors?: number;
  deposits?: number;
  deposits_amount?: number;
  ngr_amount?: number;
  commissions_amount?: number;
  commission_amount?: number;
}

interface DebugData {
  success: boolean;
  network?: any;
  monthly: MonthlyData[];
  daily: any[];
  legacy: any[];
  players: any[];
  totals: {
    commission: number;
    ngr: number;
    clicks: number;
    signups: number;
    ftds: number;
    deposits: number;
    monthlyCount: number;
    legacyCount: number;
    playersCount: number;
  };
  error?: string;
}

interface SummaryData {
  success: boolean;
  summary?: {
    total_commission: number;
    total_players: number;
    months_synced: number;
    total_ngr: number;
    current_balance: number;
    first_sync_date: string;
    last_sync_date: string;
    has_data: boolean;
  };
  error?: string;
}

export default function Debug() {
  const { token } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [debugData, setDebugData] = useState<DebugData | null>(null);
  const [summaryData, setSummaryData] = useState<SummaryData | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'monthly' | 'daily' | 'legacy' | 'players' | 'raw'>('monthly');
  const [logs, setLogs] = useState<string[]>(['Ready...']);
  const [syncing, setSyncing] = useState(false);
  const [networksList, setNetworksList] = useState<{id: string; name: string; username?: string}[]>([]);

  // Get network ID from URL or use default
  const networkId = searchParams.get('id') || DEFAULT_NETWORK_ID;
  const networkName = debugData?.network?.name || searchParams.get('name') || DEFAULT_ACCOUNT;

  const log = (msg: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [`[${timestamp}] ${msg}`, ...prev.slice(0, 50)]);
  };

  // Load list of ReferOn networks for quick switching
  const loadNetworksList = async () => {
    try {
      const resp = await fetch('/api/networks?platform_provider_id=referon&limit=200', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json() as { data?: {id: string; name: string; username?: string}[] };
      setNetworksList(data.data || []);
    } catch {
      // Ignore errors
    }
  };

  const loadData = async () => {
    setLoading(true);
    log(`Loading data for ${networkName} (${networkId})...`);

    try {
      // Load summary
      const summaryResp = await fetch(`/api/networks/${networkId}/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const summary: SummaryData = await summaryResp.json();
      setSummaryData(summary);
      log(`Summary: ${summaryResp.status} - Commission: €${summary.summary?.total_commission || 0}`);

      // Load debug stats
      const debugResp = await fetch(`/api/networks/${networkId}/debug-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const debug: DebugData = await debugResp.json();
      setDebugData(debug);
      log(`Debug: ${debugResp.status} - ${debug.totals?.monthlyCount || 0} monthly, ${debug.totals?.playersCount || 0} players`);

    } catch (err: any) {
      log(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const switchNetwork = (id: string, name: string) => {
    setSearchParams({ id, name });
    setDebugData(null);
    setSummaryData(null);
    setLogs([`Switching to ${name}...`]);
  };

  const triggerSync = async () => {
    setSyncing(true);
    log('Triggering sync...');
    try {
      const resp = await fetch(`/api/networks/${networkId}/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data: Record<string, unknown> = await resp.json();
      log(`Sync: ${resp.status} - ${JSON.stringify(data).substring(0, 200)}`);
      setTimeout(loadData, 3000);
    } catch (err: any) {
      log(`Sync error: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const fetchHistory = async () => {
    setSyncing(true);
    log('Fetching full history from 2019 (force=true to re-sync all months)...');
    try {
      const resp = await fetch(`/api/networks/${networkId}/historical-sync?start_date=2019-01-01&force=true`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data: { summary?: { months_synced_now?: number; total_players_synced?: number } } = await resp.json();
      log(`History: ${resp.status} - months: ${data.summary?.months_synced_now || 0}, players: ${data.summary?.total_players_synced || 0}`);
      setTimeout(loadData, 5000);
    } catch (err: any) {
      log(`History error: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const syncPlayers = async () => {
    setSyncing(true);
    log('Syncing players only...');
    try {
      const resp = await fetch(`/api/networks/${networkId}/historical-sync?start_date=2019-01-01&skip_stats=true`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json() as { summary?: { player_debug?: Record<string, unknown> } };
      const playerDebug = (data.summary?.player_debug || {}) as Record<string, unknown>;
      const fetchResult = (playerDebug.fetchResult || {}) as Record<string, unknown>;
      const fetchDebug = (fetchResult.debug || {}) as Record<string, unknown>;
      log(`Players: ${resp.status} - API returned: ${fetchResult.count || 0}, found: ${playerDebug.totalPlayersFound || 0}, toInsert: ${playerDebug.toInsert || 0}, toUpdate: ${playerDebug.toUpdate || 0}`);
      if (fetchDebug.pagesLoaded) {
        log(`API debug: pagesLoaded=${fetchDebug.pagesLoaded}, customersTotal=${fetchDebug.customersTotal}, customersDataLength=${fetchDebug.customersDataLength}`);
      }
      if (fetchResult.error) {
        log(`API error: ${fetchResult.error}`);
      }
      log(`Player debug: ${JSON.stringify(playerDebug)}`);
      setTimeout(loadData, 5000);
    } catch (err: any) {
      log(`Players error: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    loadData();
    loadNetworksList();
  }, [networkId]);

  const formatNumber = (num: number | undefined) => {
    return (num || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const renderMonthlyTable = () => {
    const data = debugData?.monthly || [];
    if (!data.length) return <p className="text-gray-400">No monthly data found</p>;

    let totalCommission = 0;
    let totalNgr = 0;

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-700 text-gray-300">
            <tr>
              <th className="p-3 text-left">Month</th>
              <th className="p-3 text-right">Clicks</th>
              <th className="p-3 text-right">Signups</th>
              <th className="p-3 text-right">FTDs</th>
              <th className="p-3 text-right">Deposits</th>
              <th className="p-3 text-right">NGR</th>
              <th className="p-3 text-right">Commission</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => {
              const commission = row.commissions_amount || row.commission_amount || 0;
              const ngr = row.ngr_amount || 0;
              totalCommission += commission;
              totalNgr += ngr;
              
              return (
                <tr key={i} className="border-b border-gray-700 hover:bg-gray-700/50">
                  <td className="p-3 font-mono">{row.stat_month || row.month}</td>
                  <td className="p-3 text-right">{row.clicks || 0}</td>
                  <td className="p-3 text-right">{row.registrations || row.signups || 0}</td>
                  <td className="p-3 text-right">{row.first_time_depositors || row.ftds || 0}</td>
                  <td className="p-3 text-right">€{formatNumber(row.deposits_amount || row.deposits)}</td>
                  <td className={`p-3 text-right ${ngr > 0 ? 'text-green-400' : ngr < 0 ? 'text-red-400' : 'text-gray-500'}`}>
                    €{formatNumber(ngr)}
                  </td>
                  <td className={`p-3 text-right font-semibold ${commission > 0 ? 'text-green-400' : 'text-gray-500'}`}>
                    €{formatNumber(commission)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-gray-700 font-bold">
            <tr>
              <td className="p-3">TOTAL ({data.length} months)</td>
              <td className="p-3"></td>
              <td className="p-3"></td>
              <td className="p-3"></td>
              <td className="p-3"></td>
              <td className="p-3 text-right text-green-400">€{formatNumber(totalNgr)}</td>
              <td className="p-3 text-right text-green-400">€{formatNumber(totalCommission)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  const renderLegacyTable = () => {
    const data = debugData?.legacy || [];
    if (!data.length) return <p className="text-gray-400">No legacy data found</p>;

    return (
      <div className="overflow-x-auto">
        <p className="text-gray-400 mb-2">{data.length} legacy records from 'statistics' table</p>
        <table className="w-full text-sm">
          <thead className="bg-gray-700 text-gray-300">
            <tr>
              <th className="p-3 text-left">Month</th>
              <th className="p-3 text-right">Clicks</th>
              <th className="p-3 text-right">Signups</th>
              <th className="p-3 text-right">FTDs</th>
              <th className="p-3 text-right">NGR</th>
              <th className="p-3 text-right">Commission</th>
            </tr>
          </thead>
          <tbody>
            {data.slice(0, 50).map((row: any, i: number) => {
              const commission = row.commission_amount || row.commissions_amount || row.commission_earned || 0;
              const ngr = row.ngr_amount || row.ngr || row.net_gaming_revenue || 0;
              
              return (
                <tr key={i} className="border-b border-gray-700 hover:bg-gray-700/50">
                  <td className="p-3 font-mono">{row.month}</td>
                  <td className="p-3 text-right">{row.clicks || 0}</td>
                  <td className="p-3 text-right">{row.signups || row.registrations || 0}</td>
                  <td className="p-3 text-right">{row.ftds || row.first_time_depositors || 0}</td>
                  <td className={`p-3 text-right ${ngr > 0 ? 'text-green-400' : 'text-gray-500'}`}>
                    €{formatNumber(ngr)}
                  </td>
                  <td className={`p-3 text-right font-semibold ${commission > 0 ? 'text-green-400' : 'text-gray-500'}`}>
                    €{formatNumber(commission)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPlayersTable = () => {
    const data = debugData?.players || [];
    if (!data.length) return <p className="text-gray-400">No players found in database</p>;

    return (
      <div className="overflow-x-auto">
        <p className="text-gray-400 mb-2">{data.length} players in database</p>
        <table className="w-full text-sm">
          <thead className="bg-gray-700 text-gray-300">
            <tr>
              <th className="p-3 text-left">Player ID</th>
              <th className="p-3 text-left">Country</th>
              <th className="p-3 text-left">Signup</th>
              <th className="p-3 text-left">First Deposit</th>
              <th className="p-3 text-left">Brand</th>
              <th className="p-3 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.slice(0, 50).map((row: any, i: number) => (
              <tr key={i} className="border-b border-gray-700 hover:bg-gray-700/50">
                <td className="p-3 font-mono text-xs">{row.external_player_id || row.player_id}</td>
                <td className="p-3">{row.country}</td>
                <td className="p-3">{row.signup_date}</td>
                <td className="p-3">{row.first_deposit_date || '-'}</td>
                <td className="p-3">{row.brand}</td>
                <td className="p-3">
                  <span className={`px-2 py-1 rounded text-xs ${row.status === 'active' ? 'bg-green-900 text-green-300' : 'bg-yellow-900 text-yellow-300'}`}>
                    {row.status || 'unknown'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">🔍 Debug: {networkName}</h1>
          <p className="text-gray-400">{debugData?.network?.username || 'ReferOn Account'} - ID: {networkId.substring(0, 8)}...</p>
          {networksList.length > 0 && (
            <select 
              value={networkId}
              onChange={(e) => {
                const selected = networksList.find(n => n.id === e.target.value);
                if (selected) switchNetwork(selected.id, selected.name);
              }}
              className="mt-2 bg-gray-700 text-white rounded px-3 py-1 text-sm"
            >
              {networksList.map(n => (
                <option key={n.id} value={n.id}>
                  {n.name} - {n.username || 'Unknown'}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="flex gap-3">
          <button 
            onClick={loadData} 
            disabled={loading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Refresh Data'}
          </button>
          <button 
            onClick={triggerSync} 
            disabled={syncing}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium disabled:opacity-50"
          >
            Sync Now
          </button>
          <button 
            onClick={fetchHistory} 
            disabled={syncing}
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 rounded-lg font-medium disabled:opacity-50"
          >
            Fetch History
          </button>
          <button 
            onClick={syncPlayers} 
            disabled={syncing}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-medium disabled:opacity-50"
          >
            Sync Players
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-xs text-gray-400">Total Commission</div>
          <div className="text-xl font-bold text-green-400">€{formatNumber(summaryData?.summary?.total_commission)}</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-xs text-gray-400">Players</div>
          <div className="text-xl font-bold text-blue-400">{summaryData?.summary?.total_players || 0}</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-xs text-gray-400">Months</div>
          <div className="text-xl font-bold text-purple-400">{summaryData?.summary?.months_synced || 0}</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-xs text-gray-400">NGR</div>
          <div className="text-xl font-bold text-yellow-400">€{formatNumber(summaryData?.summary?.total_ngr)}</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-xs text-gray-400">Balance</div>
          <div className="text-xl font-bold">€{formatNumber(summaryData?.summary?.current_balance)}</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-xs text-gray-400">First Sync</div>
          <div className="text-xl font-bold">{summaryData?.summary?.first_sync_date || '-'}</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-xs text-gray-400">Last Sync</div>
          <div className="text-xl font-bold">{summaryData?.summary?.last_sync_date || '-'}</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-xs text-gray-400">Has Data</div>
          <div className="text-xl font-bold">{summaryData?.summary?.has_data ? '✓' : '✗'}</div>
        </div>
      </div>

      {/* DB Stats */}
      <div className="bg-gray-800 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-2">Database Records</h3>
        <div className="flex gap-6 text-sm">
          <span>Monthly: <strong className="text-blue-400">{debugData?.totals?.monthlyCount || 0}</strong></span>
          <span>Legacy: <strong className="text-purple-400">{debugData?.totals?.legacyCount || 0}</strong></span>
          <span>Players: <strong className="text-green-400">{debugData?.totals?.playersCount || 0}</strong></span>
          <span>Calculated Commission: <strong className="text-yellow-400">€{formatNumber(debugData?.totals?.commission)}</strong></span>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-gray-800 rounded-lg p-4">
        <div className="flex gap-2 mb-4">
          {(['monthly', 'daily', 'legacy', 'players', 'raw'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium ${activeTab === tab ? 'bg-blue-600' : 'bg-gray-700 hover:bg-gray-600'}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        <div className="min-h-[300px]">
          {activeTab === 'monthly' && renderMonthlyTable()}
          {activeTab === 'legacy' && renderLegacyTable()}
          {activeTab === 'players' && renderPlayersTable()}
          {activeTab === 'daily' && (
            <div>
              <p className="text-gray-400">{debugData?.daily?.length || 0} daily records (last 60 days)</p>
              <pre className="mt-2 bg-gray-900 p-4 rounded overflow-auto max-h-96 text-xs">
                {JSON.stringify(debugData?.daily?.slice(0, 10), null, 2)}
              </pre>
            </div>
          )}
          {activeTab === 'raw' && (
            <pre className="bg-gray-900 p-4 rounded overflow-auto max-h-[500px] text-xs">
              {JSON.stringify({ summary: summaryData, debug: debugData }, null, 2)}
            </pre>
          )}
        </div>
      </div>

      {/* Debug Log */}
      <div className="bg-gray-800 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-2">Debug Log</h3>
        <div className="bg-gray-900 rounded p-3 font-mono text-xs max-h-48 overflow-y-auto">
          {logs.map((log, i) => (
            <div key={i} className="text-gray-400">{log}</div>
          ))}
        </div>
      </div>
    </div>
  );
}
